import { useState, useEffect, useCallback } from 'react';
import { getDoc } from 'firebase/firestore';
import { auth } from '../firebase.config';
import { getSettingsDoc } from './appArtifacts';

/**
 * Product decision: page-level MPIN gates are currently disabled globally.
 * Login & Security may still store MPIN settings for a future re-enable.
 * Set to false and remove early returns below to restore enforcement.
 */
export const MPIN_ACCESS_GLOBALLY_DISABLED = true;

// Hook to check if a specific page/tab requires MPIN verification
export const useMPINAccess = (page, tab = null) => {
  // Temporarily disable MPIN requirement globally
  const [requiresMPIN, setRequiresMPIN] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkMPINAccess = useCallback(async () => {
    try {
      if (MPIN_ACCESS_GLOBALLY_DISABLED) {
        setRequiresMPIN(false);
        setLoading(false);
        return;
      }
      const userId = auth.currentUser?.uid;
      if (!userId || !page) {
        setLoading(false);
        return;
      }

      const settingsRef = getSettingsDoc(userId);
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        const userData = settingsSnap.data();
        const mpinSettings = userData.mpinAccessControl;
        
        if (mpinSettings) {
          // Find the page in the MPIN settings
          let pageRequiresMPIN = false; // Default to NOT requiring MPIN
          
          for (const category of Object.keys(mpinSettings)) {
            if (mpinSettings[category] && mpinSettings[category][page]) {
              if (tab) {
                // Check specific tab - if tab is checked (true), it requires MPIN
                pageRequiresMPIN = mpinSettings[category][page][tab] === true;
              } else {
                // Check if page requires MPIN - for pages without tabs, check _directAccess
                if (mpinSettings[category][page].hasOwnProperty('_directAccess')) {
                  // Pages without tabs (like Items, Bill Templates)
                  pageRequiresMPIN = mpinSettings[category][page]._directAccess === true;
                } else {
                  // Pages with tabs - if ANY tab is checked (true), page requires MPIN
                  const tabs = Object.values(mpinSettings[category][page]);
                  pageRequiresMPIN = tabs.some(tabRequires => tabRequires === true);
                }
              }
              break;
            }
          }
          
          setRequiresMPIN(pageRequiresMPIN);
        } else {
          // No MPIN settings found, default to protected
          setRequiresMPIN(true);
        }
      } else {
        // No user settings found, default to protected
        setRequiresMPIN(true);
      }
    } catch (error) {
      console.error('Error checking MPIN access:', error);
      setError(error);
      setRequiresMPIN(true); // Default to protected on error
    } finally {
      setLoading(false);
    }
  }, [page, tab]);

  useEffect(() => {
    checkMPINAccess();
  }, [checkMPINAccess]);

  return { requiresMPIN, loading, error };
};

// Function to check if user has MPIN set
export const useMPINStatus = () => {
  const [hasMPIN, setHasMPIN] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkMPINStatus();
  }, []);

  const checkMPINStatus = async () => {
    try {
      const userId = auth.currentUser?.uid;
      if (!userId) {
        setLoading(false);
        return;
      }

      const settingsRef = getSettingsDoc(userId);
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        const userData = settingsSnap.data();
        setHasMPIN(!!userData.mpin);
      } else {
        setHasMPIN(false);
      }
    } catch (error) {
      console.error('Error checking MPIN status:', error);
      setHasMPIN(false);
    } finally {
      setLoading(false);
    }
  };

  return { hasMPIN, loading };
};

// Function to verify MPIN
export const verifyMPIN = async (inputMPIN) => {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const settingsRef = getSettingsDoc(userId);
    const settingsSnap = await getDoc(settingsRef);
    
    if (!settingsSnap.exists()) {
      throw new Error('User settings not found');
    }

    const userData = settingsSnap.data();
    const storedMPIN = userData.mpin;
    
    if (!storedMPIN) {
      throw new Error('No MPIN set for user');
    }

    // Simple base64 comparison (in production, use proper hashing)
    const hashedInput = btoa(inputMPIN);
    return hashedInput === storedMPIN;
  } catch (error) {
    console.error('Error verifying MPIN:', error);
    throw error;
  }
};

// MPIN verification modal hook
export const useMPINVerification = (page, tab = null) => {
  const { requiresMPIN, loading } = useMPINAccess(page, tab);
  const { hasMPIN } = useMPINStatus();
  const [showMPINModal, setShowMPINModal] = useState(false);
  const [mpinInput, setMpinInput] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleMPINSubmit = async (e) => {
    e.preventDefault();
    setVerificationError('');
    setIsVerifying(true);

    try {
      const isValid = await verifyMPIN(mpinInput);
      if (isValid) {
        setShowMPINModal(false);
        setMpinInput('');
        setVerificationError('');
        setIsVerifying(false);
        return true; // MPIN verification successful
      } else {
        setVerificationError('Incorrect MPIN. Please try again.');
        setMpinInput('');
        setIsVerifying(false);
        return false; // MPIN verification failed
      }
    } catch (error) {
      setVerificationError(error.message || 'Error verifying MPIN');
      setIsVerifying(false);
      return false; // MPIN verification error
    }
  };

  const openMPINModal = () => {
    if (requiresMPIN && hasMPIN) {
      setShowMPINModal(true);
    }
  };

  const closeMPINModal = () => {
    setShowMPINModal(false);
    setMpinInput('');
    setVerificationError('');
  };

  return {
    requiresMPIN,
    hasMPIN,
    loading,
    showMPINModal,
    mpinInput,
    setMpinInput,
    verificationError,
    isVerifying,
    handleMPINSubmit,
    openMPINModal,
    closeMPINModal
  };
};

// MPIN Modal Component
export const MPINVerificationModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  mpinInput, 
  setMpinInput, 
  verificationError, 
  isVerifying,
  title = "MPIN Verification Required",
  message = "Please enter your MPIN to access this section."
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-600 mb-4">{message}</p>
          
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                value={mpinInput}
                onChange={(e) => setMpinInput(e.target.value)}
                maxLength={4}
                className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="••••"
                autoFocus
              />
            </div>
            
            {verificationError && (
              <div className="text-red-600 text-sm">{verificationError}</div>
            )}
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isVerifying || !mpinInput || mpinInput.length !== 4}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {isVerifying ? 'Verifying...' : 'Verify MPIN'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
